import {
  ANALYSIS_READINESS_DECISION_SCHEMA_VERSION,
  analysisReadinessDecisionV1Schema,
  decisionAnalysisSnapshotV1Schema,
  type AnalysisReadinessDecisionV1,
  type AnalysisReadinessReasonV1,
  type DecisionAnalysisInputEnvelopeV1,
  type DecisionAnalysisSnapshotV1,
} from '../../types/decisionAnalysis';
import {
  decisionAnalysisInputDrift,
  verifyDecisionAnalysisInputEnvelopeIdentity,
  verifyDecisionAnalysisSnapshotIdentity,
  type DecisionAnalysisInputDrift,
} from './identity';

export interface EvaluateDecisionAnalysisReadinessOptions {
  evaluatedAt?: string;
}

/**
 * Name WHAT went stale (2026-08-17). The generic "The SED, intent, typed
 * preferences, authority, upstream evidence, or analysis version changed"
 * gave the operator six suspects and no verdict — live, "SED v1 · Not
 * decision-current" sent the operator down the SED-version path when the
 * actual drift was intent content + provider-authority evidence. Bound SED
 * sections are named when the drift is there; otherwise the named non-SED
 * inputs from `decisionAnalysisInputDrift.changedInputs`; the generic sentence
 * survives only as the fallback for unnamed drift.
 */
export function describeDecisionAnalysisInputDrift(
  drift: DecisionAnalysisInputDrift | null,
): string {
  const sentences: string[] = [];
  if (drift && drift.changedBoundSections.length > 0) {
    sentences.push(
      `SED evidence this analysis reads changed: ${drift.changedBoundSections.join(', ')}.`,
    );
  }
  const upstreamKinds: string[] = [];
  for (const input of drift?.changedInputs ?? []) {
    if (input === 'sed') {
      // Only emitted in the whole-document fallback (no section story).
      sentences.push('The SED evidence content changed.');
    } else if (input === 'intent') {
      sentences.push('The project intent changed.');
    } else if (input === 'typed-preferences') {
      sentences.push('Typed preferences this analysis binds changed.');
    } else if (input === 'power-strategy') {
      sentences.push('The accountable power strategy changed.');
    } else if (input === 'authority') {
      sentences.push('The bound workflow authority changed.');
    } else if (input === 'analysis-versions') {
      sentences.push('The analysis engine version changed.');
    } else {
      upstreamKinds.push(input.slice('upstream:'.length));
    }
  }
  if (upstreamKinds.length > 0) {
    sentences.push(`Upstream evidence changed: ${upstreamKinds.join(', ')}.`);
  }
  return sentences.length > 0
    ? sentences.join(' ')
    : 'The SED, intent, typed preferences, authority, upstream evidence, or analysis version changed.';
}

function reason(
  code: AnalysisReadinessReasonV1['code'],
  message: string,
): AnalysisReadinessReasonV1 {
  return { code, message };
}

function decision(input: {
  state: AnalysisReadinessDecisionV1['state'];
  canView: boolean;
  current: boolean;
  identityValid: boolean;
  qualityReady: boolean;
  narrativeReady: boolean;
  reasons: AnalysisReadinessReasonV1[];
  evaluatedAt: string;
}): AnalysisReadinessDecisionV1 {
  const canUseForDecision = (
    input.current
    && input.identityValid
    && input.qualityReady
    && input.narrativeReady
    && input.reasons.length === 0
  );
  return analysisReadinessDecisionV1Schema.parse({
    schemaVersion: ANALYSIS_READINESS_DECISION_SCHEMA_VERSION,
    state: canUseForDecision ? 'ready' : input.state,
    ready: canUseForDecision,
    canView: input.canView,
    canUseForDecision,
    current: input.current,
    identityValid: input.identityValid,
    qualityReady: input.qualityReady,
    narrativeReady: input.narrativeReady,
    reason: input.reasons[0] ?? null,
    reasons: input.reasons,
    evaluatedAt: input.evaluatedAt,
  });
}

function hasEvidenceDecisionBlock(snapshot: DecisionAnalysisSnapshotV1): boolean {
  return snapshot.semantic.quality.findings.some(finding => finding.severity === 'block'
    && (finding.code === 'source-role-truth-block' || finding.code === 'source-water-truth-block'
      || finding.code === 'executive-summary-traceability-block'
      || finding.code === 'source-report-truth-violation'));
}

function qualityReason(
  snapshot: DecisionAnalysisSnapshotV1,
): AnalysisReadinessReasonV1 | null {
  const quality = snapshot.semantic.quality;
  if (hasEvidenceDecisionBlock(snapshot)) {
    if (quality.findings.some(finding => finding.code === 'executive-summary-traceability-block' && finding.severity === 'block')) {
      return reason('hard-gate-failed', 'The reviewed executive summary requires source and claims reconciliation before it can support a decision.');
    }
    return reason('hard-gate-failed', quality.findings.some(finding => finding.code === 'source-water-truth-block' && finding.severity === 'block')
      ? 'The report’s water claims or required water disclosure do not match its saved evidence and must be revised before it can support a decision.'
      : quality.findings.some(finding => finding.code === 'source-report-truth-violation' && finding.severity === 'block')
        ? 'The report contains unresolved deterministic factual contradictions and must be revised before it can support a decision.'
      : 'The report contains source-role factual contradictions and must be revised before it can support a decision.');
  }
  switch (quality.state) {
    case 'pass':
      // AUDIT-ADVISORY (2026-08-22): hardGatesPassed=false on a snapshot is
      // the truth-audit era's stamp (the body was always present at finalize,
      // and hash-invalid snapshots mint state 'failed', handled below) — it
      // is not a readiness reason. Kept as a check only for a future genuine
      // artifact gate minted alongside state 'pass'.
      if (!quality.hardGatesPassed) {
        return reason(
          'hard-gate-failed',
          'One or more deterministic semantic-analysis hard gates failed.',
        );
      }
      return null;
    case 'held':
      // Advisory holds remain usable with their caveats. Explicit current
      // truth blockers are handled above independently of this state stamp.
      return null;
    case 'degraded':
      return reason(
        'quality-degraded',
        'The semantic analysis is degraded and cannot support a final decision.',
      );
    case 'failed':
      return reason(
        'quality-failed',
        'The semantic analysis failed its quality decision.',
      );
  }
}

function narrativeReason(
  snapshot: DecisionAnalysisSnapshotV1,
): AnalysisReadinessReasonV1 | null {
  const narrative = snapshot.semantic.narrative;
  switch (narrative.state) {
    case 'ready':
      return null;
    case 'pending':
      return reason(
        'narrative-pending',
        'Written explanatory analysis is still being prepared.',
      );
    case 'degraded':
      return reason(
        'narrative-degraded',
        `Written explanatory analysis is degraded: ${narrative.reason}`,
      );
    case 'failed':
      return reason(
        'narrative-failed',
        `Written explanatory analysis failed: ${narrative.reason}`,
      );
    case 'not-requested':
      return reason(
        'narrative-not-requested',
        'Written explanatory analysis has not been requested.',
      );
  }
}

/**
 * Evaluate the semantic decision artifact only. This boundary deliberately has
 * no report, presentation, export, artifact or PDF input.
 *
 * Preliminary structured findings remain viewable when narrative or quality
 * is incomplete, but GO/CONDITIONAL eligibility requires current immutable
 * input identity, passing quality, adequate data and ready written narrative.
 */
export function evaluateDecisionAnalysisReadiness(
  snapshotValue: unknown,
  currentInputValue: DecisionAnalysisInputEnvelopeV1 | unknown,
  options: EvaluateDecisionAnalysisReadinessOptions = {},
): AnalysisReadinessDecisionV1 {
  const evaluatedAt = options.evaluatedAt ?? new Date().toISOString();
  if (snapshotValue == null) {
    const reasons = [
      reason('analysis-missing', 'No decision-analysis snapshot is available.'),
    ];
    return decision({
      state: 'missing',
      canView: false,
      current: false,
      identityValid: false,
      qualityReady: false,
      narrativeReady: false,
      reasons,
      evaluatedAt,
    });
  }

  const parsedSnapshot = decisionAnalysisSnapshotV1Schema.safeParse(snapshotValue);
  if (!parsedSnapshot.success) {
    const reasons = [
      reason(
        'snapshot-schema-invalid',
        'The decision-analysis snapshot failed runtime schema validation.',
      ),
    ];
    return decision({
      state: 'failed',
      canView: false,
      current: false,
      identityValid: false,
      qualityReady: false,
      narrativeReady: false,
      reasons,
      evaluatedAt,
    });
  }
  const snapshot = parsedSnapshot.data;
  const identity = verifyDecisionAnalysisSnapshotIdentity(snapshot);
  if (!identity.valid) {
    const reasons = [
      reason(
        'snapshot-identity-invalid',
        identity.reasons.join(' '),
      ),
    ];
    return decision({
      state: 'failed',
      canView: false,
      current: false,
      identityValid: false,
      qualityReady: false,
      narrativeReady: false,
      reasons,
      evaluatedAt,
    });
  }

  const currentIdentity = verifyDecisionAnalysisInputEnvelopeIdentity(
    currentInputValue,
  );
  if (!currentIdentity.valid || !currentIdentity.inputHash) {
    const reasons = [
      reason(
        'current-input-invalid',
        currentIdentity.reasons.join(' ')
          || 'The current decision-analysis input envelope is invalid.',
      ),
    ];
    return decision({
      state: 'failed',
      canView: true,
      current: false,
      identityValid: true,
      qualityReady: false,
      narrativeReady: false,
      reasons,
      evaluatedAt,
    });
  }

  const reasons: AnalysisReadinessReasonV1[] = [];
  // hashVersion-aware: a v2 snapshot compared against a v2 live envelope gets
  // content-addressed SED currency (a content-identical rebuild stays
  // current); a v1 snapshot keeps its own id/version-sensitive semantics via
  // the cross-version compat projection.
  const drift = currentIdentity.input != null
    ? decisionAnalysisInputDrift(snapshot.input, currentIdentity.input)
    : null;
  const current = drift?.current ?? false;
  if (!current) {
    reasons.push(reason('input-stale', describeDecisionAnalysisInputDrift(drift)));
  }

  // AUDIT-ADVISORY (2026-08-22): an advisory-held snapshot is decision-eligible.
  // Explicit current truth, source-role, water-source and executive traceability
  // blockers independently withhold decisions. hardGatesPassed is ignored for
  // held states (truth-era stamp; see qualityReason). Without this, the state
  // chain computes 'ready' while canUseForDecision stays false and the
  // readiness schema's superRefine rejects the decision (ZodError) — the
  // exact crash the round-2 verification reproduced.
  const qualityReady = (
    (snapshot.semantic.quality.state === 'pass'
      && snapshot.semantic.quality.hardGatesPassed)
    || snapshot.semantic.quality.state === 'held'
  ) && !hasEvidenceDecisionBlock(snapshot);
  const qualityBlock = qualityReason(snapshot);
  if (qualityBlock) reasons.push(qualityBlock);

  const narrativeReady = snapshot.semantic.narrative.state === 'ready';
  const narrativeBlock = narrativeReason(snapshot);
  if (narrativeBlock) reasons.push(narrativeBlock);

  if (snapshot.semantic.adequacy.state !== 'adequate') {
    reasons.push(reason(
      'data-inadequate',
      snapshot.semantic.adequacy.reason
        ?? 'The semantic analysis does not have adequate decision evidence.',
    ));
  }

  const state: AnalysisReadinessDecisionV1['state'] = !current
    ? 'stale'
    : snapshot.semantic.quality.state === 'failed'
      || snapshot.semantic.narrative.state === 'failed'
      ? 'failed'
      : snapshot.semantic.quality.state === 'degraded'
        || snapshot.semantic.narrative.state === 'degraded'
        ? 'degraded'
        /*
         * AUDIT-ADVISORY (2026-08-22): a stamped quality.state 'held' and a
         * stamped hardGatesPassed=false are HISTORICAL truth-audit outcomes —
         * at finalize time the body was always present, so neither ever
         * encoded an artifact failure. Advisory holds remain usable; genuine
         * adequacy gaps and explicit deterministic truth blockers hold readiness.
         */
        : snapshot.semantic.adequacy.state === 'inadequate' || (!qualityReady && qualityBlock?.code === 'hard-gate-failed')
          ? 'held'
          : snapshot.semantic.narrative.state === 'pending'
            ? 'refreshing'
            : snapshot.semantic.narrative.state === 'not-requested'
              ? 'queued'
              : 'ready';

  return decision({
    state,
    canView: true,
    current,
    identityValid: true,
    qualityReady,
    narrativeReady,
    reasons,
    evaluatedAt,
  });
}

const PRINTABLE_REVIEW_HOLD_REASON_CODES = new Set<AnalysisReadinessReasonV1['code']>([
  'hard-gate-failed',
  'data-inadequate',
  'quality-held',
]);

/**
 * Separate printable-review export eligibility from decision readiness.
 * A complete, current, identity-valid single-report analysis revision whose
 * narrative is ready may be exported to PDF/DOCX for review when held only by
 * disclosed quality/truth/adequacy holds, while remaining non-ready
 * (`ready: false`, `canUseForDecision: false`) for decision use.
 */
export function canExportAnalysisForPrintableReview(
  readiness: AnalysisReadinessDecisionV1,
): boolean {
  if (
    !readiness.current
    || !readiness.identityValid
    || !readiness.canView
    || !readiness.narrativeReady
  ) {
    return false;
  }
  if (readiness.state === 'ready') {
    return readiness.ready && readiness.canUseForDecision && readiness.reasons.length === 0;
  }
  if (readiness.state === 'held') {
    return (
      readiness.reasons.length > 0
      && readiness.reasons.every(entry => PRINTABLE_REVIEW_HOLD_REASON_CODES.has(entry.code))
    );
  }
  return false;
}

export function decisionAnalysisIsReady(
  snapshot: unknown,
  currentInput: DecisionAnalysisInputEnvelopeV1 | unknown,
): boolean {
  return evaluateDecisionAnalysisReadiness(snapshot, currentInput).ready;
}

export interface ReportSemanticReadinessAdmission {
  present: boolean;
  valid: boolean;
  readiness: AnalysisReadinessDecisionV1 | null;
  /** Non-null when semantic readiness holds or invalidates the report for any calculation/decision use (ignores currency-only 'input-stale'). */
  holdReason: { code: string; message: string } | null;
  /** True when semantic readiness is currency-stale (`current === false` or `state === 'stale'`). */
  stale: boolean;
  /** True when semantic readiness is absent (legacy report) OR valid, current, ready, and decision-eligible. */
  admittedForCurrentDecision: boolean;
}

/**
 * Shared semantic readiness admission for report carriers across `reportPlan`,
 * `screeningDecisionSnapshot`, and `decisionStageReports`.
 *
 * - Legacy reports with neither `analysisReadiness` nor `analysisRevision.readiness`
 *   return `present: false`, `holdReason: null`, `stale: false`, `admittedForCurrentDecision: true`.
 * - Malformed readiness records fail closed (`holdReason: { code: 'schema-invalid', ... }`).
 * - Any non-currency hold (`held`, `failed`, `degraded`, `missing`, `canView: false`,
 *   `identityValid: false`, `qualityReady: false`, `narrativeReady: false`, or any
 *   reason other than `input-stale`) produces a non-null `holdReason`, blocking both
 *   current decision admission and saved calculation fallback.
 * - Currency-only staleness (`state === 'stale'`, `current === false`, all other
 *   readiness flags true and only `input-stale` reasons) returns `holdReason: null`
 *   (so a clean historical report can still serve as a disclosed `saved` calculation
 *   fallback) while setting `stale: true` and `admittedForCurrentDecision: false`.
 */
export function evaluateReportSemanticReadiness(
  report: {
    analysisReadiness?: unknown;
    analysisRevision?: { readiness?: unknown } | null;
  } | null | undefined,
): ReportSemanticReadinessAdmission {
  const raw = report?.analysisReadiness ?? report?.analysisRevision?.readiness;
  if (raw == null) {
    return {
      present: false,
      valid: true,
      readiness: null,
      holdReason: null,
      stale: false,
      admittedForCurrentDecision: true,
    };
  }
  const parsed = analysisReadinessDecisionV1Schema.safeParse(raw);
  if (!parsed.success) {
    return {
      present: true,
      valid: false,
      readiness: null,
      holdReason: {
        code: 'schema-invalid',
        message: 'The semantic analysis readiness record is malformed.',
      },
      stale: false,
      admittedForCurrentDecision: false,
    };
  }
  const readiness = parsed.data;
  const nonStaleReasons = readiness.reasons.filter(entry => entry.code !== 'input-stale');
  const primaryNonStale = (readiness.reason && readiness.reason.code !== 'input-stale')
    ? readiness.reason
    : nonStaleReasons[0] ?? null;
  const hasNonStaleHold = (
    !readiness.canView
    || !readiness.identityValid
    || !readiness.qualityReady
    || !readiness.narrativeReady
    || readiness.state === 'held'
    || readiness.state === 'failed'
    || readiness.state === 'degraded'
    || readiness.state === 'missing'
    || nonStaleReasons.length > 0
  );
  const holdReason = hasNonStaleHold
    ? {
        code: primaryNonStale?.code
          ?? (!readiness.identityValid ? 'schema-invalid'
            : readiness.state === 'degraded' ? 'degraded'
            : 'hard-gate-failed'),
        message: primaryNonStale?.message
          ?? readiness.reason?.message
          ?? 'This analysis must pass quality review before it can support a decision.',
      }
    : null;
  const stale = !readiness.current || readiness.state === 'stale';
  const admittedForCurrentDecision = Boolean(
    holdReason === null
      && !stale
      && readiness.current
      && readiness.ready
      && readiness.canUseForDecision,
  );
  return {
    present: true,
    valid: true,
    readiness,
    holdReason,
    stale,
    admittedForCurrentDecision,
  };
}
